// Copyright (c) 2020-2021, 2023-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Popover as MuiPopover } from '@mui/material';
import { Fade, MenuList, MenuItem, ListItemIcon } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import kebabCase from 'lodash/kebabCase';

import Ellipsis from '@shared/components/Ellipsis';
import Flex from '@shared/components/Flex';
import Avatar from '@shared/components/Avatar';
import ProfileIcon from '@shared/icons/Profile';
import LogoutIcon from '@shared/icons/Logout';
import { ROLES, ROLES_LABELS } from '@shared/constants/user';
import SystemUser from '@shared/models/system-user';
import history from '@shared/utils/history';
import ROUTES from '@shared/constants/routes';
import { getTestAttr } from '@shared/utils/common';

import AlarmsIcon from '@shared/icons/Alarms';

export interface ProfileInfoProps {
  className?: string;
  user: SystemUser;
  onLogoutClick: () => any;
}

// Class to show the user's profile section at the top right of the page.
class ProfileInfo extends React.Component<ProfileInfoProps> {
  readonly state = {
    anchorEl: null
  };

  // Show the context menu when the user clicks on the avatar.
  private handleOpen = (e) => {
    e.persist();
    this.setState((state) => ({ ...state, anchorEl: e.target }));
  }

  // Close the context menu.
  private handleClose = () => {
    this.setState((state) => ({ ...state, anchorEl: null }));
  }

  // Open the Edit Profile page when the user clicks on the Profile menu item.
  private handleGoToProfile = () => {
    history.push(ROUTES.private.profile);
    this.setState((state) => ({ ...state, anchorEl: null }));
  }

  // Open the Alarms page when the user clicks on the Alarms menu item.
  private handleGoToAlarms = () => {
    history.push(ROUTES.private.alarms);
    this.setState((state) => ({ ...state, anchorEl: null }));
  }

  // Get the text to show the user's roles.
  private get roles() {
    const { user } = this.props;
    const classes = withStyles.getClasses(this.props);

    // Combine the user's roles into a single string.
    const roles = user.roles.map((role) => ROLES_LABELS[role]).join(', ');

    return (
      <Ellipsis
        maxWidth={300}
        text={roles}
        classes={{ root: classes.role }}
      />
    );
  }

  // Configure the items for the context menu.
  private get menuItems(): Array<{ icon: React.ReactElement; text: React.ReactNode, onClick: () => any, key: string }> {
    const { onLogoutClick, user } = this.props;
    const isCustomerUser = user.roles.every((role) => ROLES.customerUser == role);

    // Menu items for customer users.
    if (isCustomerUser) {
      return [
        {
          icon: <ProfileIcon />,
          text: 'Profile',
          onClick: this.handleGoToProfile,
          key: 'ProfileIcon'
        },
        {
          icon: <LogoutIcon />,
          text: 'Log out',
          onClick: onLogoutClick,
          key: 'LogoutIcon'
        }
      ];
    }

    // Menu items for other users.
    return [
      {
        icon: <ProfileIcon />,
        text: 'Profile',
        onClick: this.handleGoToProfile,
        key: 'Profile'
      },
      {
        icon: <AlarmsIcon />,
        text: 'Alerts',
        onClick: this.handleGoToAlarms,
        key: 'Alarm'
      },
      {
        icon: <LogoutIcon />,
        text: 'Log out',
        onClick: onLogoutClick,
        key: 'Logout'
      }
    ];
  }

  render() {
    const { user } = this.props;
    const classes = withStyles.getClasses(this.props);
    const { anchorEl } = this.state;

    return (
      <>
        {/* Show the user's name and roles. */}
        <Flex className={classes.nameWrapper} direction="column">
          <div className={classes.fullName}>{user.fullName}</div>
          {this.roles}
        </Flex>

        {/* Show the user's avatar and the drop down/up arrow */}
        <Flex
          alignItems="center"
          wrap="nowrap"
          onClick={this.handleOpen}
        >
          <Avatar
            {...getTestAttr('Layout', 'header', 'avatar')}
            src={user.img}
            fullName={user.fullName}
            className={classes.avatar}
          />
          
          <ArrowDropUpIcon
            className={classes.avatarArrow}
            style={{ transform: anchorEl ? 'none' : 'rotate(180deg)' }}
          />
        </Flex>

        {/* Wrapper to show/hide the context menu */}
        <MuiPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          classes={{ root: classes.root, paper: classes.menuWrapper }}
          TransitionComponent={Fade}
          transitionDuration={{
            appear: 200,
            enter: 200,
            exit: 0
          }}
          onClose={this.handleClose}
        >
          {/* The menu items */}
          <MenuList classes={{ root: classes.menu }}>
            {this.menuItems.map((item) => (
              <MenuItem
                {...getTestAttr('Layout', 'header', `avatar-${kebabCase(item.text?.toString())}`)}
                key={item.key}
                onClick={item.onClick}
                className={classes.menuItem}
              >
                <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                {item.text}
              </MenuItem>
            ))
            }
          </MenuList>
        </MuiPopover>
      </>
    );
  }
}

const ProfileInfoStyled = withStyles(
  ProfileInfo, 
  (theme, props) => ({
    "root": {
      '&:before': {
        position: 'absolute',
        width: 15,
        height: 15,
        top: 58,
        right: 112,
        borderTop: `1px solid ${theme.palette.colors.greyLight6}`,
        borderLeft: `1px solid ${theme.palette.colors.greyLight6}`,
        content: '""',
        backgroundColor: theme.palette.colors.white,
        transform: 'translate(76px, -1px) rotate(45deg)',
        zIndex: 2
      }
    },
    "menuWrapper": {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(12),
      border: `1px solid ${theme.palette.colors.greyLight6}`,
      borderRadius: '4px',
    },
    "nameWrapper": {
      marginRight: theme.spacing(2)
    },
    "fullName": {
      fontSize: 12,
      fontWeight: 600
    },
    "role": {
      fontSize: 10,
      color: theme.palette.colors.greyLight3,
      textAlign: 'end'
    },
    "logoutBtn": {
      marginLeft: theme.spacing(6),
    },
    "avatar": {
      cursor: 'pointer',
      margin: 0,
      width: '40px',
      height: '40px',
      background: theme.palette.colors.primaryAccent,
      border: `2px solid ${theme.palette.colors.greyLight6}`,
    },
    "avatarArrow": {
      cursor: 'pointer',
      color: theme.palette.colors.greyDark2,
      transition: `${theme.transitions.duration.standard}s`
    },
    "menu": {
      padding: theme.spacing(2)
    },
    "menuIcon": {
      paddingRight: theme.spacing(2),
      minWidth: 0,

      '& svg': {
        width: '16px',
        height: '16px',
      }
    },
    "menuItem": {
      margin: 0,
      padding: theme.spacing(2),
      minWidth: 100,
      fontSize: '15px',
      lineHeight: 1.2,
      borderRadius: '4px',

      '&:hover': {
        color: theme.palette.colors.primaryAccent,

        '& svg': {
          fill: theme.palette.colors.primaryAccent
        }
      }
    }
  })
);

export default ProfileInfoStyled;